import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  UploadFileForm,
} from "../../../components";
import { useDate, useRequest } from "../../../hooks";
import {
  ClientProps,
  DocumentProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";
import DataContext from "../../../context/DataContext";

const isSuccessful = (r: any) => r?.success || r?.succes;

export default function ChemicalTrackerAdmin() {
  const { handleRequest } = useRequest();
  const { getDateFromString } = useDate();
  const { clientOptions, fetchDataClient, onDeleteDocument } = useContext(DataContext);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);

  useEffect(() => { fetchDataClient(); }, []);

  useEffect(() => {
    setTimeout(() => { setIsError(false); setIsSuccess(false); }, 4000);
  }, [isError, isSuccess]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedClient(client);
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `chemical-list/client/${client.id}`, options: opts }),
    ])
      .then(([wellsRes, chemRes]) => {
        const wellData: WellProps[] = isSuccessful(wellsRes) ? wellsRes.data : [];
        setWells(wellData);
        setWellsLoading(false);
        const wellMap = new Map<number, string>(wellData.map((w) => [w.id as number, w.name]));
        const chemRows: DocRow[] = (isSuccessful(chemRes) ? chemRes.data : []).map(
          (item: any, idx: number) => ({
            rowId: `c-${item.id ?? idx}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.idWell,
            wellName: wellMap.get(item.idWell) ?? "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
          }),
        );
        setRows(chemRows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchChemicalByKey = (key: string, name: string, download: boolean) => {
    setIsCreatingReport(true);
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    handleRequest({ endpoint: "pdf-chemical-adm", options: opts })
      .then((res) => {
        if (res.success) {
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          if (download) {
            const link = document.createElement("a");
            link.href = url; link.setAttribute("download", name);
            document.body.appendChild(link); link.click();
          } else {
            window.open(url);
          }
        } else setIsError(true);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsCreatingReport(false));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.path) fetchChemicalByKey(row.path, row.customName, true);
  };

  const handleUploadChemical = async ({
    client, well, file, customName, installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      customName && formData.append("customName", customName);
      installationDate && formData.append("installationDate", installationDate.toISOString());
      formData.append("type", "chemical-tracker");
      formData.append("file", file, file.name);
      const res = await (
        await fetch(process.env.REACT_APP_DEV_API + "/chemical-upload", {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        })
      ).json();
      if (res.success) { setIsSuccess(true); selectedClient && handleClientChange(selectedClient); }
      else setIsError(true);
    } catch { setIsError(true); }
    finally { setIsCreatingReport(false); }
  };

  return (
    <>
      <DocumentsPortalLayout
        title="Chemical Trackers"
        subtitle="Browse, filter and download chemical trackers"
        wells={wells}
        wellsLoading={wellsLoading}
        rows={rows}
        rowsLoading={rowsLoading}
        selectedWellId={selectedWellId}
        onWellSelect={setSelectedWellId}
        onDownload={handleDownload}
        showSource={false}
        noWellMessage="Select a client to load their chemical trackers"
        isAdmin
        clientOptions={clientOptions}
        onClientChange={handleClientChange}
        onUpload={() => setIsModalOpen(true)}
        onOpenInNew={(row) => row.path && !row.path.includes(".xlsx") && fetchChemicalByKey(row.path, row.customName, false)}
        onDelete={(row) => { setIdToDelete(String(row.rowId.replace("c-", ""))); setIsOpenAlert(true); }}
        isError={isError}
      />

      {isCreatingReport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1300 }} />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={[]}
              type="chemical-tracker"
              fetchDataWells={(id) => handleRequest({ endpoint: `well/client/${id}`, options: { method: "GET" } })}
              onUploadFile={(v: DocumentProps) => handleUploadChemical(v)}
              onCancel={() => setIsModalOpen(false)}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this Chemical Tracker from the Cloud?</div>}
          onAccept={() => {
            onDeleteDocument("chemical-file", idToDelete, () => selectedClient && handleClientChange(selectedClient));
            setIdToDelete(""); setIsOpenAlert(false);
          }}
          onCancel={() => { setIsOpenAlert(false); setIdToDelete(""); }}
        />
      )}
    </>
  );
}
